import type { AdditionalDetailsFormValues } from '../../../types/registration.types';

/**
 * A registration this browser sent whose account it has not yet found. Written
 * just before the request; one still present when the page loads means the
 * page was left — reloaded, most likely — before that, and the request is not
 * cancelled by that: entity-service carries on and may create the account.
 *
 * <p>Cleared only once the answer is known: a refusal (nothing was created),
 * the account in hand, or a reloaded page's search whose last read of the
 * account list said it is not there. A 2xx alone does not clear it — the
 * account can lag behind the answer in the user's list, and a reload in that
 * window must still search for it. A lost answer (5xx, 408, timeout, network)
 * does not either. Past `PENDING_REGISTRATION_TTL_MS` it is ignored, and
 * `endSession` clears it with the rest of storage.
 */
const PENDING_REGISTRATION_KEY = 'pendingRegistration';

/** Older than this, a pending registration has long since answered one way or the other. */
const PENDING_REGISTRATION_TTL_MS = 5 * 60_000;

export type RegistrationFields = Pick<
  AdditionalDetailsFormValues,
  'companyName' | 'firstName' | 'lastName'
>;

export interface PendingRegistration extends RegistrationFields {
  userId: string;
  startedAt: number;
}

export const savePending = (userId: string, values: RegistrationFields) => {
  const pending: PendingRegistration = {
    userId,
    companyName: values.companyName,
    firstName: values.firstName,
    lastName: values.lastName,
    startedAt: Date.now(),
  };
  try {
    localStorage.setItem(PENDING_REGISTRATION_KEY, JSON.stringify(pending));
  } catch (error: unknown) {
    // A safeguard, not a precondition: a full or blocked storage must not stop
    // the registration itself.
    console.warn('post-registration: could not record the pending registration', error);
  }
};

/** This user's pending registration, unless it is stale or unreadable. */
export const readPending = (userId: string): PendingRegistration | null => {
  try {
    const pending = JSON.parse(
      localStorage.getItem(PENDING_REGISTRATION_KEY) ?? 'null',
    ) as PendingRegistration | null;
    return pending?.userId === userId &&
      Date.now() - pending.startedAt < PENDING_REGISTRATION_TTL_MS
      ? pending
      : null;
  } catch {
    return null;
  }
};

export const clearPending = () => localStorage.removeItem(PENDING_REGISTRATION_KEY);
